import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiCheckboxCircleLine,
  RiFireFill,
  RiLoader3Line
} from '@remixicon/react'
import { format, parseISO } from 'date-fns'
import { type ReactNode, useMemo, useState } from 'react'

import { DetailFlowBadge } from '../badges/detail-flow-badge'
import { DetailStatusBadge } from '../badges/detail-status-badge'
import { TooltipCell } from '../basic-table/cell/tooltip-cell'
import { ColorBadge } from '../color-badge'
import { ImageWithFallback } from '../image-with-fallback'

import { AssignDetailCart } from './assign-detail-cart'
import { AssignDetailDate } from './assign-detail-date'
import { DetailStageSelector } from './detail-stage-selector'
import { useFlows } from '@/api/flows/flows'
import type { Detail } from '@/api/orders/orders-types'
import { useMarkLogAsDone } from '@/api/production-logs/production-logs'
import { ImageEditPopup } from '@/components/image-canvas/image-edit-popup'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { isCompletedDetailStatus } from '@/components/common/selectors/detail-status-selector/config/statuses'
import { DATE_FORMATS } from '@/constants/app'
import { useDetailId } from '@/hooks/use-query-states'
import { cn } from '@/lib/utils'
import { ViewToggle } from '@/pages/scheduling/components/scheduling-list/view-toggle'
import { useMultistep } from '@/store/multistep'
import { useImageCacheStore } from '@/store/image-cache'
import { usePrintStickers } from '@/store/print-stickers'
import {
  getAllProdLogsDone,
  getCurrentDetailImage,
  getCurrentFlow,
  getFlowInfo,
  getTargetFlow,
  getVariobendFile
} from '@/utils/details'

type AllowedControls = 'date' | 'stage' | 'cart' | 'done-button'

interface DetailCardProps {
  detail: Detail
  allowedControls?: AllowedControls[]
  hideEdit?: boolean
  hideMachinesInfo?: boolean
  hideViewToggle?: boolean
  /** List view: render down to the description, rest behind an expander. */
  collapsible?: boolean
  /** Controlled expander state; falls back to internal state when omitted. */
  expanded?: boolean
  onExpandedChange?: (next: boolean) => void
  className?: string
  children?: ReactNode
}

export const DetailCard = ({
  detail,
  allowedControls = ['cart', 'date', 'stage'],
  hideEdit = false,
  hideMachinesInfo = false,
  hideViewToggle = false,
  collapsible = false,
  expanded: expandedProp,
  onExpandedChange,
  className,
  children
}: DetailCardProps) => {
  const [selfExpanded, setSelfExpanded] = useState(false)
  const expanded = expandedProp ?? selfExpanded
  const setExpanded = onExpandedChange ?? setSelfExpanded
  const [detailIdOpen] = useDetailId()
  const { isSelected, toggleSelectedDetail, selectedDetails, multiStep } = useMultistep()

  const { setPrintDetail } = usePrintStickers()

  const isDetailOpen = detailIdOpen === detail.autoid

  const scrollCardIntoView = (card: HTMLDivElement | null) => {
    if (isDetailOpen) {
      card?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const bustParam = useImageCacheStore((s) => s.getBustParam(detail.autoid))
  const currentImage = useMemo(
    () => getCurrentDetailImage(detail, bustParam),
    [detail.additional_files, detail.variobend_files, bustParam]
  )

  const currentLog = detail?.production_logs?.at(-1)

  const { data: flows } = useFlows()

  const { nextFlow, currentFlow, isLastFlow, currentFlows } = getFlowInfo({ detail, flows: flows?.results || [] })

  const allLogsDone = getAllProdLogsDone({ logs: detail?.production_logs ?? [], flows: currentFlows })

  // const allBundlesDone = detail?.production_bundles.length

  const { handleMarkDone, isPending } = useMarkLogAsDone({
    onSuccess: (logId) => {
      // Resolve from the completed log, not from item.status: onSuccess runs
      // before the invalidated item refetches, so status still says the flow is
      // in progress and any status-derived flow is off by one.
      const doneInven = detail?.production_logs?.find((log) => log.id === logId)?.flow_inven
      if (doneInven === 'PUNCHTIME' || doneInven === 'FT') return
      setPrintDetail({
        detail,
        inven: doneInven,
        labels: doneInven === 'CUTTIME' ? ['id'] : ['bundle']
      })
    }
  })

  const haveMachines = Object.keys(detail?.standard_times ?? {}).length

  const selectedTargetFlow = selectedDetails.length
    ? getTargetFlow({ detail: selectedDetails[0], flows: flows?.results })
    : null
  const detailTargetFlow = getTargetFlow({ detail, flows: flows?.results })

  const ableToSelect =
    !selectedTargetFlow ||
    selectedTargetFlow.inven === detailTargetFlow?.inven ||
    isSelected(detail)

  const showSelectionDisabled = multiStep && !ableToSelect

  const isBypassProduction = !Object.keys(detail?.standard_times ?? {}).length

  const cardElement = (
    <Card
      onClick={() => {
        if (!allLogsDone && ableToSelect && haveMachines) {
          toggleSelectedDetail(detail)
        }
      }}
      ref={scrollCardIntoView}
      className={cn(
        'relative min-w-74 gap-2 rounded-2xl leading-none outline-2 outline-transparent transition-all',
        detail.item?.status === 'complete' || detail.item?.status === 'ready_to_ship'
          ? 'outline-green-200'
          : '',
        detail.item?.status === 'shipped' ? 'outline-purple-200' : '',
        !!detail?.item?.is_blocked ? 'bg-red-100 outline-red-300' : '',
        isDetailOpen || isSelected(detail) ? 'outline-blue-200' : '',
        showSelectionDisabled ? 'cursor-not-allowed opacity-50' : '',
        collapsible ? 'gap-1.5 p-2.5' : '',
        className
      )}
    >
      <CardHeader className='flex-row items-start justify-between gap-2'>
        <div className='flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1.5'>
          <div className='flex items-center gap-2'>
            <CardTitle>
              {detail?.inven} <span className='font-normal text-blue-200'>x {detail?.quan}</span>
            </CardTitle>
            <Separator
              orientation='vertical'
              className='h-3.5'
            />
            <span>{detail?.invoice}</span>
            <Separator
              orientation='vertical'
              className='h-3.5'
            />
            <div className='text-grey-500 text-sm'>
              {detail.width || '-'} x{' '}
              <span className={cn(detail.heightd !== 120 ? 'font-bold text-red-300' : '')}>
                {detail.heightd}
              </span>
            </div>
          </div>
          <ColorBadge
            className='min-w-0 max-w-full'
            color={detail.color}
            colorHex={detail.color_hex}
          />
        </div>

        {hideViewToggle ? null : <ViewToggle detail={detail} />}
      </CardHeader>

      <Separator className='bg-grey-300 w-full' />

      <CardContent className={cn('space-y-2.5 text-sm leading-none', collapsible ? 'space-y-1.5' : '')}>
        <div className='flex items-center gap-2'>
          <span>
            {detail.ship_date ? format(parseISO(detail.ship_date), DATE_FORMATS.dots) : '-'} {detail?.ship_time}
          </span>
          <Separator
            className='h-3.5 max-h-3.5'
            orientation='vertical'
          />
          <span
            className={cn(
              'flex items-center gap-0.5 rounded-sm',
              detail?.ship_type === 'SET OUTSIDE'
                ? 'text-white-100 bg-red-300 py-0.5 pr-1 pl-0.5 text-[10px]'
                : ''
            )}
          >
            {detail?.ship_type === 'SET OUTSIDE' ? <RiFireFill className='-mt-px size-3' /> : null}

            <span className='mt-px'>{detail?.ship_type ?? '-'}</span>
          </span>
        </div>

        <div className='flex items-center gap-2'>
          {detail.customer ? (
            <TooltipCell
              className='max-w-full truncate text-left'
              title={detail.customer || '-'}
            >
              {detail?.customer}
            </TooltipCell>
          ) : (
            '-'
          )}
          <Separator
            className='h-3.5 max-h-3.5'
            orientation='vertical'
          />
          {detail?.ship_tag ? (
            <TooltipCell
              className='max-w-full truncate text-left'
              title={detail.ship_tag}
            >
              {detail?.ship_tag}
            </TooltipCell>
          ) : (
            '-'
          )}
        </div>

        <TooltipCell
          className='text-grey-700 max-w-full truncate'
          title={detail?.descr ?? ''}
        >
          {detail?.descr}
        </TooltipCell>

        {collapsible ? (
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
            className='text-grey-500 hover:text-blue-200 flex cursor-pointer items-center gap-0.5 text-[11px]'
          >
            {expanded ? (
              <RiArrowUpSLine className='size-3.5' />
            ) : (
              <RiArrowDownSLine className='size-3.5' />
            )}
            <span>{expanded ? 'Hide details' : 'Show details'}</span>
          </button>
        ) : null}

        {collapsible && !expanded ? null : (
          <>
            <div className='relative flex items-center justify-center overflow-hidden rounded-md border p-1'>
              {!hideEdit && getVariobendFile({ files: detail.variobend_files ?? [] }) ? (
                <ImageEditPopup
                  className='absolute top-1 right-1'
                  data={detail}
                />
              ) : null}
              <ImageWithFallback
                className='aspect-[2_/_1] size-full object-contain'
                src={currentImage}
                alt='image'
              />
            </div>

            {isBypassProduction || hideMachinesInfo ? null : <CurrentDetailMachinesInfo detail={detail} />}

            {allowedControls.includes('date') && !isBypassProduction ? (
              <AssignDetailDate detail={detail} />
            ) : null}
            {allowedControls.includes('stage') ? (
              <div className='flex items-center gap-2'>
                <DetailStatusBadge
                  className='w-1/2'
                  key={detail?.item?.status}
                  status={detail?.item?.status ?? 'cutting'}
                />
                {isCompletedDetailStatus(detail.item?.status) || isBypassProduction ? null : (
                  <DetailStageSelector detail={detail} />
                )}
              </div>
            ) : null}
            {!isCompletedDetailStatus(detail.item?.status) && allowedControls.includes('cart') && !!nextFlow ? (
              <AssignDetailCart
                detail={detail}
                disabled={!currentLog?.stage_id || !!currentLog.end_time || !!detail.item?.is_blocked}
              />
            ) : null}
            {!allowedControls.includes('done-button') || allLogsDone ? null : (
              <Button
                className='w-full'
                size='sm'
                onClick={(e) => {
                  e.stopPropagation()

                  if (currentLog?.cart_id) {
                    handleMarkDone({ cartId: +currentLog?.cart_id, logId: currentLog?.id! })
                  } else {
                    handleMarkDone({ cartId: 0, logId: currentLog?.id! })
                  }
                }}
                disabled={
                  !currentLog?.start_time ||
                  isPending ||
                  !!currentLog?.end_time ||
                  (!isLastFlow && !currentLog.cart_id) ||
                  !!detail.item?.is_blocked
                }
              >
                {isPending ? <RiLoader3Line /> : <RiCheckboxCircleLine />}
                <span>Mark {currentFlow?.name} Done</span>
              </Button>
            )}
            {children}
          </>
        )}
      </CardContent>
    </Card>
  )

  if (showSelectionDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{cardElement}</TooltipTrigger>
        <TooltipContent>
          Can't combine with current selection — batch goes to{' '}
          <span className='font-medium'>{selectedTargetFlow?.name ?? '—'}</span>, this item goes to{' '}
          <span className='font-medium'>{detailTargetFlow?.name ?? '—'}</span>.
        </TooltipContent>
      </Tooltip>
    )
  }

  return cardElement
}

interface CurrentDetailMachinesInfoProps {
  detail: Detail
}

const CurrentDetailMachinesInfo = ({ detail }: CurrentDetailMachinesInfoProps) => {
  const { data: flows, isLoading: isFlowLoading } = useFlows()

  const currentFlows =
    flows?.results.filter((flow) =>
      detail?.standard_times ? Object?.keys(detail?.standard_times)?.includes(flow?.inven) : false
    ) ?? []

  const currentFlow = getCurrentFlow(detail?.item?.status, currentFlows)

  const maxCuttingStartTime = useMemo(() => {
    if (!currentFlow?.inven || !detail?.min_start_times) {
      return ''
    }
    return detail.min_start_times[currentFlow.inven] || ''
  }, [currentFlow?.inven, detail?.min_start_times])

  if (isFlowLoading) {
    return <Skeleton className='h-14 rounded-md p-2' />
  }

  return (
    <div className='bg-grey-100 h-14 rounded-md border p-2'>
      <div className='flex items-center gap-1'>
        <span className='text-grey-700 text-xs'>Max start Time: </span>
        <span>
          {maxCuttingStartTime ? format(new Date(maxCuttingStartTime), DATE_FORMATS.shortDateWithTime) : '-'}
        </span>
      </div>
      <div className='flex items-center gap-2'>
        {currentFlows?.length ? (
          currentFlows.map((flow) => {
            const currentLog = detail.production_logs?.find((log) => log.flow_inven === flow?.inven)
            const isDone = !!currentLog?.end_time

            return (
              <div
                className='flex items-center gap-2'
                key={flow.id}
              >
                <DetailFlowBadge
                  done={isDone}
                  className={cn('bg-transparent p-0 text-xs [&_svg]:size-3.5')}
                  flowInven={flow?.inven}
                />
                {flow.id !== currentFlows[currentFlows.length - 1]?.id ? (
                  <Separator className='bg-grey-500 !w-2' />
                ) : null}
              </div>
            )
          })
        ) : (
          <div className='text-sm'>No machines</div>
        )}
      </div>
    </div>
  )
}
